
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ArrowLeft, Search, Filter, Download, ExternalLink, CheckCircle, Clock, ChevronDown, Check, X, AlertCircle, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { formatCurrency, formatDate } from '../../../utils/helpers';

const PaymentReport = ({
    paymentSubmissions, // Contains both pending and approved submissions
    walletTransactions = [], // Added for manual payments
    players,
    onBack,
    isTreasurer = false,
    isAdmin = false
}) => {
    // State for filters

    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [amountRange, setAmountRange] = useState({ min: '', max: '' });
    const [selectedPlayers, setSelectedPlayers] = useState([]); // Multi-select ID array
    const [statusFilter, setStatusFilter] = useState('all');
    const [subStatusFilter, setSubStatusFilter] = useState('all'); // Awaited, None/Standard
    const [paymentModeFilter, setPaymentModeFilter] = useState('all');

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: 'submittedAt', direction: 'desc' });

    // Handle Sort
    const handleSort = (key) => {
        setSortConfig(current => {
            if (current.key === key) {
                // Toggle direction
                return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            // New key, default to ascending (except for date and amount which usually start desc)
            const defaultDesc = ['submittedAt', 'amount'].includes(key);
            return { key, direction: defaultDesc ? 'desc' : 'asc' };
        });
    };

    // Player Multi-select State
    const [playerSearchTerm, setPlayerSearchTerm] = useState('');
    const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsPlayerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter players for dropdown
    const filteredPlayersList = useMemo(() => {
        return players.filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [players, playerSearchTerm]);

    // Handle Player Selection
    const togglePlayerSelection = (playerId) => {
        if (selectedPlayers.includes(playerId)) {
            setSelectedPlayers(p => p.filter(id => id !== playerId));
        } else {
            setSelectedPlayers(p => [...p, playerId]);
        }
    };

    const toggleSelectAllPlayers = () => {
        if (selectedPlayers.length === filteredPlayersList.length) {
            setSelectedPlayers([]);
        } else {
            setSelectedPlayers(filteredPlayersList.map(p => p.id));
        }
    };


    // Handle Sub Status Update (Treasurer or Admin)
    const handleUpdateSubStatus = async (submissionId, newSubStatus) => {
        if (!isTreasurer && !isAdmin) return;

        try {
            const subRef = doc(db, 'paymentSubmissions', submissionId);
            await updateDoc(subRef, {
                subStatus: newSubStatus
            });
            // Telemetry or toast could go here
        } catch (error) {
            console.error("Error updating sub-status:", error);
            alert("Failed to update status. Please try again.");
        }
    };

    // Process Data
    const reportData = useMemo(() => {
        // 1. Process Standard Payment Submissions
        const submissionData = paymentSubmissions.map(sub => {
            const player = players.find(p => p.id === sub.playerId || p.userId === sub.playerId);
            const memberName = sub.playerName || player?.name || sub.playerEmail || 'Unknown';
            const playerId = sub.playerId || player?.id || 'unknown';

            // Normalize Payment Mode
            const mode = sub.paymentMethod || 'Unknown';

            // Determine Status for Display
            let displayStatus = 'Pending';
            let subStatus = 'Pending';
            const normalizedMode = mode.toLowerCase();

            if (sub.status === 'approved') {
                displayStatus = 'Paid';

                // Sub Status Logic: Persisted > Calculated Fallback
                if (sub.subStatus) {
                    subStatus = sub.subStatus;
                } else if (normalizedMode.includes('benevity')) {
                    subStatus = 'Awaited';
                } else {
                    subStatus = 'Received';
                }
            } else if (sub.status === 'rejected' || sub.status === 'denied') {
                displayStatus = 'Denied';
                subStatus = 'Denied';
            } else {
                displayStatus = 'Pending';
                subStatus = 'Pending';
            }

            return {
                id: sub.id,
                memberName,
                playerId,
                mode,
                amount: sub.amount || 0,
                status: sub.status,
                displayStatus,
                subStatus,
                proofUrl: sub.receiptURL,
                submittedAt: sub.submittedAt ? new Date(sub.submittedAt) : null,
                receivedAt: sub.fundsReceivedAt ? new Date(sub.fundsReceivedAt) : null,
                notes: sub.notes,
                adminNote: sub.treasurerNote || sub.denialReason || ''
            };
        });

        // 2. Process Manual Payments & Waivers from WalletTransactions
        // FILTER: usage of submissionId to prevent duplicates from approved submissions
        const manualData = (walletTransactions || [])
            .filter(t =>
                (t.type === 'payment' || t.type === 'waiver') &&
                t.status === 'approved' &&
                t.amount < 0 &&
                !t.isDonation &&
                !t.submissionId // Only include if NOT linked to a submission
            )
            .map(t => {
                const player = players.find(p => p.id === t.userId || p.userId === t.userId);
                const memberName = t.playerName || player?.name || t.playerEmail || 'Unknown';
                const playerId = t.userId || player?.id || 'unknown';
                let mode = t.type === 'waiver' ? 'Waiver' : (t.paymentMethod || 'Manual Adjustment');

                // Helper to infer mode from text
                const inferMode = (text) => {
                    if (!text) return null;
                    const lowerText = text.toLowerCase();
                    if (lowerText.includes('paypal')) return 'PayPal';
                    if (lowerText.includes('venmo')) return 'Venmo';
                    if (lowerText.includes('zelle')) return 'Zelle';
                    if (lowerText.includes('benevity')) return 'Benevity';
                    if (lowerText.includes('cash')) return 'Cash';
                    if (lowerText.includes('check')) return 'Check';
                    return null;
                };

                // If admin_recorded, try to find better mode from description or note
                if (mode.toLowerCase() === 'admin_recorded') {
                    const inferred = inferMode(t.description) || inferMode(t.note);
                    if (inferred) {
                        mode = inferred;
                    } else {
                        // Clean up display if we can't infer (remove underscore)
                        mode = 'Admin Recorded';
                    }
                }

                return {
                    id: t.id,
                    memberName,
                    playerId,
                    mode,
                    amount: Math.abs(t.amount || 0),
                    status: 'approved',
                    displayStatus: t.type === 'waiver' ? 'Waived' : 'Paid',
                    subStatus: t.type === 'waiver' ? 'Waived' : 'Received',
                    proofUrl: null,
                    submittedAt: t.createdAt ? new Date(t.createdAt) : null,
                    receivedAt: t.receivedDate ? new Date(t.receivedDate) : (t.type !== 'waiver' && t.createdAt ? new Date(t.createdAt) : null),
                    notes: t.description || t.note || (t.type === 'waiver' ? t.waiverReason : ''),
                    adminNote: t.treasurerNote || ''
                };
            });

        return [...submissionData, ...manualData];
    }, [paymentSubmissions, walletTransactions, players]);



    // Filtering Logic
    const filteredData = useMemo(() => {
        return reportData.filter(item => {
            // Member Filter (Multi-select)
            if (selectedPlayers.length > 0) {
                if (!selectedPlayers.includes(item.playerId)) return false;
            }

            // Date Range
            if (dateRange.start) {
                const start = new Date(dateRange.start);
                start.setHours(0, 0, 0, 0);
                if (!item.submittedAt || item.submittedAt < start) return false;
            }
            if (dateRange.end) {
                const end = new Date(dateRange.end);
                end.setHours(23, 59, 59, 999);
                if (!item.submittedAt || item.submittedAt > end) return false;
            }

            // Amount Range
            if (amountRange.min !== '' && item.amount < parseFloat(amountRange.min)) return false;
            if (amountRange.max !== '' && item.amount > parseFloat(amountRange.max)) return false;


            // Status Filter
            if (statusFilter !== 'all') {
                if (statusFilter === 'paid' && item.displayStatus !== 'Paid') return false;
                if (statusFilter === 'pending' && item.displayStatus !== 'Pending') return false;
                if (statusFilter === 'denied' && item.displayStatus !== 'Denied') return false;
                if (statusFilter === 'waived' && item.displayStatus !== 'Waived') return false;
            }

            // Sub Status Filter (Explicit)
            if (subStatusFilter !== 'all') {
                if (subStatusFilter === 'awaited' && item.subStatus !== 'Awaited') return false;
                if (subStatusFilter === 'received' && item.subStatus !== 'Received') return false;
            }

            // Payment Mode Filter
            if (paymentModeFilter !== 'all') {
                if (item.mode.toLowerCase() !== paymentModeFilter.toLowerCase()) return false;
            }

            return true;
        });
    }, [reportData, selectedPlayers, dateRange, amountRange, statusFilter, subStatusFilter, paymentModeFilter]);

    // Summary Calculation (DEPENDS ON FILTERED DATA)
    const summaryStats = useMemo(() => {
        let received = 0;
        let pending = 0;
        let denied = 0;
        let awaited = 0;
        let waived = 0;

        filteredData.forEach(item => {
            const amount = parseFloat(item.amount) || 0;

            // Prioritize Awaited subStatus
            if (item.subStatus === 'Awaited') {
                awaited += amount;
            } else {
                // Otherwise use displayStatus
                switch (item.displayStatus) {
                    case 'Paid':
                        received += amount;
                        break;
                    case 'Pending':
                        pending += amount;
                        break;
                    case 'Denied':
                        denied += amount;
                        break;
                    case 'Waived':
                        waived += amount;
                        break;
                }
            }
        });

        // Nett = Received + Pending + Awaited
        const nett = received + pending + awaited;

        return { received, pending, denied, awaited, waived, nett };
    }, [filteredData]);

    // Sorting
    const sortedData = useMemo(() => {
        const sorted = [...filteredData].sort((a, b) => {
            const { key, direction } = sortConfig;
            let comparison = 0;

            switch (key) {
                case 'memberName':
                    comparison = a.memberName.localeCompare(b.memberName);
                    break;
                case 'submittedAt':
                    // Handle null dates
                    const dateA = a.submittedAt ? a.submittedAt.getTime() : 0;
                    const dateB = b.submittedAt ? b.submittedAt.getTime() : 0;
                    comparison = dateA - dateB;
                    break;
                case 'receivedAt':
                    const recvA = a.receivedAt ? a.receivedAt.getTime() : 0;
                    const recvB = b.receivedAt ? b.receivedAt.getTime() : 0;
                    comparison = recvA - recvB;
                    break;
                case 'mode':
                    comparison = a.mode.localeCompare(b.mode);
                    break;
                case 'amount':
                    comparison = a.amount - b.amount;
                    break;
                case 'displayStatus':
                    comparison = a.displayStatus.localeCompare(b.displayStatus);
                    break;
                case 'subStatus':
                    // Handle null/undefined subStatus
                    const subA = a.subStatus || '';
                    const subB = b.subStatus || '';
                    comparison = subA.localeCompare(subB);
                    break;
                default:
                    comparison = 0;
            }

            return direction === 'asc' ? comparison : -comparison;
        });

        return sorted;
    }, [filteredData, sortConfig]);

    // Helper for Sort Icon
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="text-gray-400 ml-1" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={14} className="text-blue-600 ml-1" />
            : <ArrowDown size={14} className="text-blue-600 ml-1" />;
    };

    // Export Handler
    const handleExport = () => {
        const header = "Member Name,Payment Date,Received Date,Mode of Payment,Amount,Status,Sub Status,Member Notes,Treasurer Notes,Proof URL\n";
        const rows = sortedData.map(row => {
            return `"${row.memberName}", "${row.submittedAt ? formatDate(row.submittedAt) : '-'}", "${row.receivedAt ? formatDate(row.receivedAt) : '-'}", "${row.mode}", ${row.amount},${row.displayStatus},${row.subStatus}, "${row.notes || ''}", "${row.adminNote || ''}", "${row.proofUrl || ''}"`;
        }).join("\n");

        const csvContent = "data:text/csv;charset=utf-8," + header + rows;

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "payments_report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft size={24} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Payments Report</h1>
                        <p className="text-gray-500">Track all member payments and statuses</p>
                    </div>
                </div>
                <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Download size={18} />
                    Export CSV
                </button>
            </div>

            {/* Summary Totals */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
                {/* Received */}
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
                    <p className="text-sm font-medium text-gray-500">Received</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(summaryStats.received)}</p>
                </div>

                {/* Pending */}
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-amber-500">
                    <p className="text-sm font-medium text-gray-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(summaryStats.pending)}</p>
                </div>

                {/* Awaited */}
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
                    <p className="text-sm font-medium text-gray-500">Awaited</p>
                    <p className="text-2xl font-bold text-purple-700">{formatCurrency(summaryStats.awaited)}</p>
                </div>

                {/* Denied */}
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
                    <p className="text-sm font-medium text-gray-500">Denied</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(summaryStats.denied)}</p>
                </div>

                {/* Waived (New) */}
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-500">
                    <p className="text-sm font-medium text-gray-500">Waived</p>
                    <p className="text-2xl font-bold text-gray-700">{formatCurrency(summaryStats.waived)}</p>
                </div>

                {/* Nett */}
                <div className="bg-blue-50 rounded-xl shadow-sm p-4 border-l-4 border-blue-600">
                    <p className="text-sm font-medium text-blue-900">Total <span className="text-xs font-normal opacity-75">(Expected)</span></p>
                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(summaryStats.nett)}</p>
                </div>
            </div>


            {/* Filters */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6 font-medium text-black space-y-4">
                {/* Row 1: Search, Date, Amount */}
                <div className="flex flex-wrap items-center gap-4">
                    {/* Member Multi-Select */}
                    <div className="relative min-w-[250px]" ref={dropdownRef}>
                        <div
                            className="flex items-center justify-between w-full p-2 bg-white border rounded-lg cursor-pointer hover:border-blue-500 font-medium text-black"
                            onClick={() => setIsPlayerDropdownOpen(!isPlayerDropdownOpen)}
                        >
                            <span className="truncate">
                                {selectedPlayers.length === 0
                                    ? 'Filter by Members'
                                    : `${selectedPlayers.length} Members Selected`}
                            </span>
                            <ChevronDown size={16} className="text-gray-500" />
                        </div>

                        {isPlayerDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
                                <div className="p-2 border-b">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                        <input
                                            type="text"
                                            placeholder="Search members..."
                                            value={playerSearchTerm}
                                            onChange={(e) => setPlayerSearchTerm(e.target.value)}
                                            className="w-full pl-8 pr-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500 font-medium text-black"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="p-2 border-b bg-gray-50 flex justify-between">
                                    <button
                                        onClick={toggleSelectAllPlayers}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        {selectedPlayers.length === filteredPlayersList.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                    <span className="text-xs text-gray-500">{filteredPlayersList.length} found</span>
                                </div>
                                <div className="overflow-y-auto flex-1 p-1">
                                    {filteredPlayersList.map(player => (
                                        <div
                                            key={player.id}
                                            onClick={() => togglePlayerSelection(player.id)}
                                            className="flex items-center gap-2 p-2 hover:bg-blue-50 cursor-pointer rounded text-sm"
                                        >
                                            <div className={`w - 4 h - 4 border rounded flex items - center justify - center 
                                                ${selectedPlayers.includes(player.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'} `}>
                                                {selectedPlayers.includes(player.id) && <Check size={12} className="text-white" />}
                                            </div>
                                            <span className="text-gray-700">{player.name}</span>
                                        </div>
                                    ))}
                                    {filteredPlayersList.length === 0 && (
                                        <div className="p-4 text-center text-gray-400 text-sm">No members found</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Date Range */}
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                            className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black"
                            placeholder="Start Date"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                            className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black"
                            placeholder="End Date"
                        />
                    </div>

                    {/* Amount Range */}
                    <div className="flex items-center gap-2">
                        <div className="relative w-24">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                                type="number"
                                placeholder="Min"
                                value={amountRange.min}
                                onChange={(e) => setAmountRange({ ...amountRange, min: e.target.value })}
                                className="w-full pl-6 pr-2 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black"
                            />
                        </div>
                        <span className="text-gray-400">-</span>
                        <div className="relative w-24">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                                type="number"
                                placeholder="Max"
                                value={amountRange.max}
                                onChange={(e) => setAmountRange({ ...amountRange, max: e.target.value })}
                                className="w-full pl-6 pr-2 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black"
                            />
                        </div>
                    </div>
                </div>

                {/* Row 2: Statuses, Mode */}
                <div className="flex flex-wrap items-center gap-4">
                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black min-w-[150px]"
                    >
                        <option value="all">All Statuses</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="denied">Denied</option>
                        <option value="waived">Waived</option>
                    </select>

                    {/* Sub Status Filter */}
                    <select
                        value={subStatusFilter}
                        onChange={(e) => setSubStatusFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black min-w-[150px]"
                    >
                        <option value="all">All Sub Statuses</option>
                        <option value="received">Received</option>
                        <option value="awaited">Awaited</option>
                    </select>

                    {/* Payment Mode Filter */}
                    <select
                        value={paymentModeFilter}
                        onChange={(e) => setPaymentModeFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-black min-w-[150px]"
                    >
                        <option value="all">All Payment Modes</option>
                        <option value="zelle">Zelle</option>
                        <option value="venmo">Venmo</option>
                        <option value="benevity">Benevity</option>
                        <option value="paypal">PayPal</option>
                    </select>

                    {/* Clear Filters */}
                    {(selectedPlayers.length > 0 || dateRange.start || dateRange.end || amountRange.min || amountRange.max || statusFilter !== 'all' || subStatusFilter !== 'all' || paymentModeFilter !== 'all') && (
                        <button
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setAmountRange({ min: '', max: '' });
                                setSelectedPlayers([]);
                                setStatusFilter('all');
                                setSubStatusFilter('all');
                                setPaymentModeFilter('all');
                            }}
                            className="text-red-600 text-sm hover:underline flex items-center gap-1"
                        >
                            <X size={14} /> Clear Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b border-gray-200 text-gray-700 text-sm">
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('memberName')}>
                                <div className="flex items-center">Member Name <SortIcon columnKey="memberName" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('submittedAt')}>
                                <div className="flex items-center">Payment Date <SortIcon columnKey="submittedAt" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('receivedAt')}>
                                <div className="flex items-center">Received Date <SortIcon columnKey="receivedAt" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('mode')}>
                                <div className="flex items-center">Mode of Payment <SortIcon columnKey="mode" /></div>
                            </th>
                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('amount')}>
                                <div className="flex items-center justify-end">Amount <SortIcon columnKey="amount" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('displayStatus')}>
                                <div className="flex items-center justify-center">Status <SortIcon columnKey="displayStatus" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('subStatus')}>
                                <div className="flex items-center justify-center">Sub Status <SortIcon columnKey="subStatus" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center">Treasurer Notes</th>
                            <th className="p-4 font-semibold text-center">Proof of Payment</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sortedData.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="p-8 text-center text-gray-500">
                                    No records found matching filters.
                                </td>
                            </tr>
                        ) : (
                            sortedData.map(row => (
                                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 font-medium text-gray-900">{row.memberName}</td>
                                    <td className="p-4 text-gray-500 text-sm">
                                        {row.submittedAt ? formatDate(row.submittedAt) : '-'}
                                    </td>
                                    <td className="p-4 text-gray-500 text-sm">
                                        {row.receivedAt ? formatDate(row.receivedAt) : '-'}
                                    </td>
                                    <td className="p-4 text-gray-700 capitalize">{row.mode}</td>
                                    <td className="p-4 text-right font-semibold text-gray-900">{formatCurrency(row.amount)}</td>
                                    <td className="p-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold
                                            ${row.displayStatus === 'Paid' ? 'bg-green-100 text-green-800' :
                                                row.displayStatus === 'Waived' ? 'bg-gray-100 text-gray-800' :
                                                    row.displayStatus === 'Denied' ? 'bg-red-100 text-red-800' :
                                                        'bg-amber-100 text-amber-800'
                                            }`}>
                                            {row.displayStatus}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center">
                                        {row.subStatus === 'Awaited' ? (
                                            <span
                                                onClick={(e) => {
                                                    if (isTreasurer || isAdmin) {
                                                        e.stopPropagation();
                                                        if (window.confirm(`Mark funds for ${row.memberName} as Received? This will clear the "Awaited" status.`)) {
                                                            handleUpdateSubStatus(row.id, 'Received');
                                                        }
                                                    }
                                                }}
                                                className={`text-xs font-medium px-2 py-1 rounded border border-purple-200 bg-purple-50 text-purple-700
                                                    ${(isTreasurer || isAdmin) ? 'cursor-pointer hover:bg-purple-100 hover:border-purple-300' : 'cursor-default'}`}
                                                title={(isTreasurer || isAdmin) ? "Click to mark funds as Received" : "Funds Awaited"}
                                            >
                                                Awaited
                                            </span>
                                        ) : (
                                            <span className={`text-sm ${row.subStatus === 'Received' ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                {row.subStatus || '-'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-4 text-sm text-gray-600 max-w-xs break-words">
                                        {row.adminNote || '-'}
                                    </td>
                                    <td className="p-4 text-center">
                                        {row.proofUrl ? (
                                            <a
                                                href={row.proofUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                            >
                                                <ExternalLink size={14} /> View
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 text-sm">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PaymentReport;
